import React from "react";
import PropTypes from "prop-types";
import { Card, CardContent, Typography, Box } from "@mui/material";
import EMPChip from "../EMPChip/EMPChip";
import { styled } from "@mui/material/styles";
import EMPButton from "../EMPButton/EMPButton";
import { useTranslation } from "react-i18next";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

const StyledCard = styled(Card, {
  name: "EMPActionCard",
  slot: "root",
})(() => ({
  background: "linear-gradient(135deg, #F5F6F7 0%, #FFFFFF 100%)",
  height: "100%",
}));

const StyledCardContent = styled(CardContent, {
  name: "EMPActionCard",
  slot: "content",
})(({ theme }) => ({
  padding: theme.spacing(0),
  display: "flex",
  flexDirection: "column",
  "&:last-child": {
    padding: theme.spacing(0),
  },
}));

// Opt-in typography only: keep each status's existing background/text colors.
const lincolnStatusBadgeSx = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  height: "24px",
  padding: "0 8px",
  "& .MuiChip-label": {
    fontFamily: "ProximaNovaRegular, Arial, sans-serif",
    fontSize: "16px",
    fontWeight: 400,
    lineHeight: "24px",
    padding: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
};

const EMPActionCard = ({
  testId,
  hasActionButton,
  onClick,
  title,
  text,
  buttonText,
  buttonType,
  status,
  completedDate,
  presentation,
  isLincolnOrderDetails,
}) => {
  const { t } = useTranslation();
  let chipLabel;
  let chipColor;

  // Set chipLabel and chipColor based on status
  if (status === "action-required") {
    chipLabel = t("common.actionStatus.actionRequired");
    chipColor = "error";
  } else if (status === "action-completed") {
    chipLabel = completedDate
      ? `${t("common.actionStatus.completedOn")} ${completedDate}`
      : t("common.actionStatus.completed");
    chipColor = "success";
  } else if (status === "action-not-available") {
    chipLabel = t("common.actionStatus.notAvailableYet");
    chipColor = "default";
  }

  if (presentation === "banner") {
    return (
      <StyledCard
        data-testid={testId}
        elevation={0}
        sx={{ background: "#ECEEEF", borderRadius: 2, height: "auto", p: 0 }}
      >
        <StyledCardContent
          sx={{
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "stretch", sm: "center" },
            gap: 4,
            px: { xs: 4, sm: 6 },
            py: 4,
            "&:last-child": { px: { xs: 4, sm: 6 }, py: 4 },
          }}
        >
          <Box
            sx={{ display: "flex", alignItems: "flex-start", gap: 4, flex: 1 }}
          >
            <InfoOutlinedIcon
              sx={{ color: "#485B65", fontSize: 20, mt: 0.5, flexShrink: 0 }}
            />
            <Box sx={{ minWidth: 0, overflowWrap: "anywhere" }}>
              <Typography
                variant="body1"
                sx={{
                  color: "text.secondary",
                  fontFamily: "ProximaNovaSemibold, Arial, sans-serif",
                  fontWeight: 600,
                }}
              >
                {title}
              </Typography>
              <Typography variant="body1" sx={{ color: "text.secondary" }}>
                {text}
              </Typography>
            </Box>
          </Box>
          {hasActionButton && (
            <EMPButton
              variant={buttonType}
              testId={`${testId}-button`}
              onClick={onClick}
              sx={{
                alignSelf: { xs: "flex-start", sm: "center" },
                flexShrink: 0,
                ml: { xs: 9, sm: 0 },
                borderRadius: "4px",
                minHeight: 48,
                minWidth: 0,
                padding: "12px 32px",
                fontFamily: "ProximaNovaSemibold, Arial, sans-serif",
                fontSize: "16px",
                fontWeight: 600,
                lineHeight: "24px",
                letterSpacing: 0,
                textTransform: "none",
                ...(buttonType === "outlined"
                  ? {
                      backgroundColor: "transparent",
                      color: "#242B2D",
                      border: "1px solid #242B2D",
                      "&:hover": { backgroundColor: "rgba(36, 43, 45, 0.08)" },
                    }
                  : {
                      backgroundColor: "#242B2D",
                      color: "#FFFFFF",
                      "&:hover": { backgroundColor: "#1B2123" },
                    }),
                "&.Mui-focusVisible": {
                  outline: "2px solid #485B65",
                  outlineOffset: 3,
                },
              }}
            >
              {buttonText}
            </EMPButton>
          )}
        </StyledCardContent>
      </StyledCard>
    );
  }

  return (
    <StyledCard data-testid={testId} elevation={0}>
      <StyledCardContent sx={{ height: "100%" }}>
        {chipLabel && chipColor && (
          <Box sx={{ mb: 4 }}>
            <EMPChip
              label={chipLabel}
              labelColor={chipColor}
              testId="test-chip"
              sx={isLincolnOrderDetails ? lincolnStatusBadgeSx : undefined}
            />
          </Box>
        )}
        <Box>
          <Typography
            variant="h6"
            variantMapping={{ h6: "p" }}
            sx={{
              color: "text.secondary",
              mb: 1,
              ...(isLincolnOrderDetails && {
                fontFamily: "ProximaNovaSemibold, Arial, sans-serif",
                fontWeight: 600,
              }),
            }}
          >
            {title}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              ...(isLincolnOrderDetails && {
                fontFamily: "ProximaNovaRegular, Arial, sans-serif",
                fontWeight: 400,
              }),
            }}
          >
            {text}
          </Typography>
        </Box>
        {hasActionButton && (
          <Box sx={{ mt: 6, flex: 1, display: "flex", alignItems: "flex-end" }}>
            <Box
              sx={{
                flex: "0 0 auto",
              }}
            >
              <EMPButton
                variant={buttonType}
                testId={`${testId}-button`}
                onClick={onClick}
              >
                {buttonText}
              </EMPButton>
            </Box>
          </Box>
        )}
      </StyledCardContent>
    </StyledCard>
  );
};

EMPActionCard.propTypes = {
  testId: PropTypes.string,
  hasActionButton: PropTypes.bool,
  onClick: PropTypes.func,
  title: PropTypes.node,
  text: PropTypes.node,
  buttonText: PropTypes.node,
  buttonType: PropTypes.string,
  status: PropTypes.oneOf([
    "action-required",
    "action-completed",
    "action-not-available",
  ]),
  completedDate: PropTypes.string,
  presentation: PropTypes.oneOf(["card", "banner"]),
  isLincolnOrderDetails: PropTypes.bool,
};

EMPActionCard.defaultProps = {
  testId: undefined,
  hasActionButton: false,
  onClick: undefined,
  title: null,
  text: null,
  buttonText: null,
  buttonType: "primary",
  status: undefined,
  completedDate: undefined,
  presentation: "card",
  isLincolnOrderDetails: false,
};

export default EMPActionCard;
